import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { APP_FONT_SIZE, APP_LINE_HEIGHT } from '../../constants/typography';

export const TRAINING_TABLE_OVERVIEW_MIN_WIDTH = 1180;
export const TRAINING_TABLE_SESSIONS_MIN_WIDTH = 1000;

/** Standalone filter row above the table card (not inside the table Paper). */
export const trainingFilterBarSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  flexWrap: 'wrap',
  alignItems: 'center',
  mb: 2,
};

/** Shared field styling for toolbar search + level filter */
export const trainingToolbarFilterFieldSx: SxProps<Theme> = {
  bgcolor: 'background.paper',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
  '& .MuiInputBase-input': {
    fontSize: APP_FONT_SIZE.sm,
    lineHeight: APP_LINE_HEIGHT.normal,
  },
  '& .MuiInputLabel-root': {
    fontSize: APP_FONT_SIZE.sm,
  },
};

export const trainingToolbarSearchFieldSx: SxProps<Theme> = {
  ...trainingToolbarFilterFieldSx,
  minWidth: 220,
  maxWidth: 360,
  flex: '1 1 220px',
};

export const trainingToolbarLevelSelectSx: SxProps<Theme> = {
  ...trainingToolbarFilterFieldSx,
  minWidth: 180,
  maxWidth: 220,
  flex: '0 0 180px',
};

export const trainingToolbarHintSx: SxProps<Theme> = {
  flex: '1 1 200px',
  minWidth: 160,
  maxWidth: 420,
  ml: { md: 'auto' },
  lineHeight: 1.45,
};

export const trainingTableEmptyMessageSx: SxProps<Theme> = {
  py: 4,
  textAlign: 'center',
};

export const trainingTableSx = (minWidth: number): SxProps<Theme> => ({
  minWidth,
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: APP_LINE_HEIGHT.normal,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    bgcolor: (t) =>
      t.palette.mode === 'light'
        ? alpha(t.palette.primary.main, 0.04)
        : alpha(t.palette.common.white, 0.04),
    borderBottom: (t) => `1px solid ${alpha(t.palette.primary.main, 0.16)}`,
    py: 1.6,
    px: 2,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-body': {
    fontSize: APP_FONT_SIZE.sm,
    lineHeight: APP_LINE_HEIGHT.normal,
    py: 1.35,
    px: 2,
    verticalAlign: 'middle',
    color: 'text.primary',
    borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body': {
    bgcolor: (t) =>
      t.palette.mode === 'light'
        ? alpha(t.palette.text.primary, 0.018)
        : alpha(t.palette.common.white, 0.02),
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-body': {
    bgcolor: (t) =>
      t.palette.mode === 'light'
        ? alpha(t.palette.primary.main, 0.05)
        : alpha(t.palette.primary.main, 0.12),
  },
  '& .MuiTableRow-root:last-child .MuiTableCell-body': {
    borderBottom: 0,
  },
  '& .MuiTableSortLabel-root': {
    color: 'inherit',
    '&:hover': { color: 'text.primary' },
    '&.Mui-active': {
      color: 'primary.main',
      '& .MuiTableSortLabel-icon': { color: 'primary.main' },
    },
  },
});

export const trainingHeadCellSx = (width?: number | string): SxProps<Theme> => ({
  ...(width != null ? { width, minWidth: width } : {}),
});

export const trainingBodyCellSx = (
  isPlaceholder: boolean,
  extra?: SxProps<Theme>,
): SxProps<Theme> => [
  ...(Array.isArray(extra) ? extra : extra ? [extra] : []),
  isPlaceholder && {
    color: 'text.disabled',
    fontWeight: 300,
    letterSpacing: '0.04em',
  },
];

export const trainingSortLabelEndSx = {
  width: '100%',
  justifyContent: 'flex-end',
  '& .MuiTableSortLabel-icon': { opacity: 0.5 },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
} as const;

export const trainingSortLabelCenterSx = {
  width: '100%',
  justifyContent: 'center',
  '& .MuiTableSortLabel-icon': { opacity: 0.5 },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
} as const;

export const trainingTableContainerSx: SxProps<Theme> = {
  maxHeight: { xs: 'none', md: 'calc(100vh - 320px)' },
  overflowX: 'auto',
  bgcolor: 'background.paper',
};

export const trainingSessionsTableContainerSx: SxProps<Theme> = {
  maxHeight: { xs: 'none', md: 'calc(100vh - 340px)' },
  overflowX: 'auto',
  bgcolor: 'background.paper',
};

export const trainingPaperSx: SxProps<Theme> = {
  width: '100%',
  overflow: 'hidden',
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: (t) =>
    t.palette.mode === 'light'
      ? `0 1px 2px ${alpha(t.palette.common.black, 0.04)}, 0 8px 24px ${alpha(t.palette.common.black, 0.06)}`
      : t.shadows[4],
};

export const trainingActionsCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  overflow: 'visible',
  textOverflow: 'clip',
};

export const trainingActionButtonSx: SxProps<Theme> = {
  borderRadius: '10px',
  width: 34,
  height: 34,
};

/** Overview column widths for fixed table layout */
export const overviewColumnMinWidth = {
  full_name: '16%',
  username: '16%',
  phone: '12%',
  level: '11%',
  hours: '9%',
  sessions: '8%',
  last_training: '11%',
  actions: 96,
} as const;
